import { ApiError } from "./api";
import {
  API_ERROR_CODE_I18N_KEY_MAP,
  API_ERROR_CODE_I18N_PATTERN_RULES,
  API_ERROR_CODE_I18N_REASON_KEY_MAP,
} from "./error-code-i18n";
// Error-code dictionaries are owned per app through scoped transports
// (`@airange/shared/i18n-<app>`). This entry only carries the common template
// and the shared access/session dictionaries so importing shared error helpers
// never drags every business error dictionary into the bundle.
import { hasMessage, type MessageParams, t } from "./i18n-error-code-common-runtime";

const USER_CANCELLED_ACTION = "user_cancelled_action";
function resolveApiErrorMessage(error: ApiError, fallback: string) {
  const translated = getApiErrorCodeMessage(error.code, error.params);
  if (translated) {
    return translated;
  }
  return fallback;
}

export type ErrorDisplay = {
  primaryText: string;
  diagnosticText: string | null;
  code: string | null;
  requestId: string | null;
  traceId: string | null;
  params: Record<string, unknown> | null;
  contractMissing: string | null;
};

function toMessageParams(params: Record<string, unknown> | null): MessageParams | undefined {
  if (!params) {
    return undefined;
  }
  const normalizedEntries = Object.entries(params).flatMap(([key, value]) => {
    if (
      value === null ||
      value === undefined ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return [[key, value] as const];
    }
    return [];
  });
  return normalizedEntries.length > 0
    ? (Object.fromEntries(normalizedEntries) as MessageParams)
    : undefined;
}

function getApiErrorCodePatternMessage(
  normalizedCode: string,
  params?: Record<string, unknown> | null,
) {
  for (const rule of API_ERROR_CODE_I18N_PATTERN_RULES) {
    if (!normalizedCode.startsWith(rule.prefix)) {
      continue;
    }
    const reasonCode = normalizedCode.slice(rule.prefix.length);
    if (!rule.reasonCodes.includes(reasonCode)) {
      continue;
    }
    const reasonI18nKey = API_ERROR_CODE_I18N_REASON_KEY_MAP[reasonCode];
    if (!reasonI18nKey || !hasMessage(reasonI18nKey) || !hasMessage(rule.resourceI18nKey)) {
      return null;
    }
    return t(reasonI18nKey, {
      ...(toMessageParams(params ?? null) ?? {}),
      resource: t(rule.resourceI18nKey),
    });
  }
  return null;
}

export function getErrorMessage(error: unknown, fallback = t("common.messages.requestFailed")) {
  return getErrorDisplay(error, fallback).primaryText;
}

function formatApiErrorTraceSuffix(error: ApiError) {
  const parts = [
    error.requestId ? `request_id: ${error.requestId}` : null,
    error.traceId ? `trace_id: ${error.traceId}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? ` (${parts.join(", ")})` : "";
}

export function getErrorDisplay(
  error: unknown,
  fallback = t("common.messages.requestFailed"),
): ErrorDisplay {
  if (error instanceof ApiError) {
    const traceSuffix = formatApiErrorTraceSuffix(error);
    if (error.code) {
      const base = resolveApiErrorMessage(error, fallback);
      return {
        primaryText: `${base} [${error.code}]${traceSuffix}`,
        diagnosticText: error.diagnosticMessage,
        code: error.code,
        requestId: error.requestId,
        traceId: error.traceId,
        params: error.params,
        contractMissing: null,
      };
    }
    if (error.diagnosticMessage) {
      return {
        primaryText: `CONTRACT_MISSING:api_error.code${traceSuffix}`,
        diagnosticText: error.diagnosticMessage,
        code: null,
        requestId: error.requestId,
        traceId: error.traceId,
        params: error.params,
        contractMissing: "CONTRACT_MISSING:api_error.code",
      };
    }
    return {
      primaryText: `${fallback}${traceSuffix}`,
      diagnosticText: null,
      code: null,
      requestId: error.requestId,
      traceId: error.traceId,
      params: error.params,
      contractMissing: null,
    };
  }
  if (error instanceof Error) {
    return {
      primaryText: fallback,
      diagnosticText: null,
      code: null,
      requestId: null,
      traceId: null,
      params: null,
      contractMissing: null,
    };
  }
  return {
    primaryText: fallback,
    diagnosticText: null,
    code: null,
    requestId: null,
    traceId: null,
    params: null,
    contractMissing: null,
  };
}

export function getApiErrorCodeMessage(
  code: string | null | undefined,
  params?: Record<string, unknown> | null,
) {
  const normalizedCode = typeof code === "string" && code.trim() ? code.trim() : "";
  if (!normalizedCode) {
    return null;
  }
  const translationKey = API_ERROR_CODE_I18N_KEY_MAP[normalizedCode];
  if (translationKey && hasMessage(translationKey)) {
    return t(translationKey, toMessageParams(params ?? null));
  }
  const patternMessage = getApiErrorCodePatternMessage(normalizedCode, params);
  if (patternMessage) {
    return patternMessage;
  }
  return t("common.errors.apiCodeUnknown", { code: normalizedCode });
}

export function getApiErrorStructuredParams(error: unknown) {
  if (!(error instanceof ApiError)) {
    return null;
  }
  return error.params;
}

export class UserCancelledActionError extends Error {
  constructor() {
    super(USER_CANCELLED_ACTION);
    this.name = "UserCancelledActionError";
  }
}

export function createUserCancelledActionError() {
  return new UserCancelledActionError();
}

export function isUserCancelledActionError(error: unknown) {
  return error instanceof UserCancelledActionError;
}
